import {
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";

import { HttpMainApi } from "../interface/main-api";
import { userState } from "../interface/MainInterface";
import LeftMenu from "./LeftMenu";

const MENU_NAME_BY_PATH: Record<string, string> = {
  "/mobileinbody": "인바디",
  "/gyrotest": "자이로 테스트",
  "/gyro": "자이로",
  "/reservationscreen": "골프 예약",
  "/reservationscreenend": "골프 예약 확인",
  "/reservationgolf": "골프장 예약",
  "/reservationgolfend": "골프장 예약 내역",
  "/reservationrobotcleaner": "창문 청소기 예약",
  "/reservationrobotcleanerend": "창문 청소기 예약 내역",
  "/reservationbookcafe": "북카페",
  "/reservationbookcafe/reserve": "북카페 예약",
  "/reservationbookcafeend": "북카페 예약 내역",
  "/reservationtabletennis": "탁구장 예약",
  "/reservationtabletennisend": "탁구장 신청 상태",
  "/weather": "날씨",
  "/reservationstudy": "독서실 예약",
  "/reservationstudyend": "독서실 예약 내역",
  "/reservationhealth": "헬스장 신청",
  "/reservationhealthend": "헬스장 신청 내역",
  "/reservationlocker": "락커룸 신청",
  "/reservationlockerend": "락커룸 신청 내역",
  "/screenadmin": "시설 예약 관리",
  "/reservation": "시설 신청",
  "/reservationend": "신청 내역",
  "/reservationnext": "일괄 신청",
  "/reservationaccount": "청구서 확인",
  "/mobilemypage": "마이페이지",
  "/mobilepasschpage": "비밀번호 변경",
  "/kvsplayer": "영상",
  "/mobilerealtime": "실시간 현황",
  "/userlist": "회원관리",
  "/userfingerlist": "지문 관리",
  "/adminreserv": "예약관리",
  "/lockerpassword": "락커 비밀번호 관리",
  "/mobilebooklist": "작은 도서관",
  "/mobilebooktemplate": "작은 도서관",
  "/mobilebookscan": "도서 대여·반납",
  "/mobilebookguide": "도서 이용 방법",
  "/mobilebookstats": "도서 대여 현황",
  "/integratedaccountlist": "통합청구서",
  "/bookcafereservationstatus": "북카페 예약 현황",
  "/robotcleanerrental": "로봇 대여",
  "/robotcleanerrentalstatus": "대여 현황",
  "/accountdetaillist": "관리비 청구 상세",
  "/usedetaillist": "사용 내역",
  "/adminmanual": "관리자 매뉴얼",
  "/adminstats": "커뮤니티 대시보드",
  "/adminbook": "도서 등록",
  "/adminbookbarcode": "바코드 등록",
  "/mobilecommunityinfo": "커뮤니티 안내",
  "/mobilescreenview": "스크린골프",
};

const getMenuName = (selectedMenu: string) => {
  if (selectedMenu === "/mobilebookstats/loans") return "대여중인 도서";
  if (selectedMenu === "/mobilebookstats/library-loans") return "전체 대여중인 도서";
  if (selectedMenu === "/mobilebookstats/popular-books") return "인기 도서";
  if (selectedMenu.startsWith("/mobilebooktemplate/category/")) return "작은 도서관";
  if (selectedMenu.startsWith("/reservationbookcafe/guide/")) return "북카페 이용 안내";
  if (selectedMenu.startsWith("/adminstats/")) return "커뮤니티 대시보드";

  return MENU_NAME_BY_PATH[selectedMenu] ?? "메뉴";
};

const mainApi = new HttpMainApi();
const BOOK_LOANS_CHANGED_EVENT = "book-loans-changed";

const HeaderBookIcon = () => (
  <svg data-testid="header-book-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 6.3c-1.73-1.07-3.72-1.43-6-1.07v12.9c2.28-.36 4.27 0 6 1.07m0-12.9c1.73-1.07 3.72-1.43 6-1.07v12.9c-2.28-.36-4.27 0-6 1.07m0-12.9v12.9"
      stroke="currentColor"
      strokeWidth="1.45"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeaderTicketIcon = () => (
  <svg data-testid="header-ticket-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5.7 5.75h12.6a1.1 1.1 0 0 1 1.1 1.1v3.03a2.12 2.12 0 0 0 0 4.24v3.03a1.1 1.1 0 0 1-1.1 1.1H5.7a1.1 1.1 0 0 1-1.1-1.1v-3.03a2.12 2.12 0 0 0 0-4.24V6.85a1.1 1.1 0 0 1 1.1-1.1Z"
      stroke="currentColor"
      strokeWidth="1.45"
      strokeLinejoin="round"
    />
    <path d="M9.15 8.3v1.15m0 2.3v1.15m0 2.3v.7" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" />
  </svg>
);

const getFutureBookCafeReservationCount = (rows: any[], serverNow: string) => {
  const serverNowMs = new Date(serverNow).getTime();
  if (Number.isNaN(serverNowMs)) return 0;

  const reservationIds = new Set<string>();
  rows.forEach((row) => {
    if (row?.reservState !== "신청" || !row?.reservationId) return;

    const reservDate = String(row.reservDate || "");
    const slot = String(row.slot || "");
    const slotStartMs = new Date(
      `${reservDate.slice(0, 4)}-${reservDate.slice(4, 6)}-${reservDate.slice(6, 8)}T${slot}:00+09:00`,
    ).getTime();

    if (!Number.isNaN(slotStartMs) && slotStartMs > serverNowMs) {
      reservationIds.add(row.reservationId);
    }
  });

  return reservationIds.size;
};

const getKoreaCalendarDayIndex = (date: Date) => {
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: string) => Number(parts.find((item) => item.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day")) / 86_400_000;
};

const getOverdueBookLoanCount = (rows: any[]) => {
  const today = getKoreaCalendarDayIndex(new Date());
  if (today === null) return 0;

  return rows.filter((row) => {
    const dueDate = getKoreaCalendarDayIndex(new Date(row?.dueAt));
    return dueDate !== null && today > dueDate;
  }).length;
};

interface propsType {
  userState: userState;
  winSize: string;
  selectedMenu: string;
  isMenuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
}

const Header = (props: propsType, ref: any) => {
  let navigate = useNavigate();
  const LeftMenuRef: any = useRef();
  const [bookCafeReservationCount, setBookCafeReservationCount] = useState(0);
  const [overdueBookLoanCount, setOverdueBookLoanCount] = useState(0);

  // 헤더 메뉴 버튼 클릭시 좌측 메뉴 활성/비활성화
  const clickhandleDrawer = () => {
    props.toggleMenu();
  };

  // 메뉴 클릭시 페이지 이동
  const move = useCallback(
    (loc: string) => {
      props.closeMenu();
      // 페이지 이동
      navigate(loc);
    },
    [navigate, props]
  );
  const getLeftCount = () => {
    LeftMenuRef.current.getLeftCount();
  };

  // 부모 Component에서 접근 가능하도록 함수 전달.
  useImperativeHandle(ref, () => ({
    getLeftCount,
  }));

  useEffect(() => {
    let isCurrent = true;

    const loadBookCafeReservationCount = async () => {
      if (!props.userState.isAuth || !props.userState.userId) {
        if (isCurrent) setBookCafeReservationCount(0);
        return;
      }

      try {
        const response = await mainApi.get_my_book_cafe_reservations({ userId: props.userState.userId });
        const rows = Array.isArray(response?.response?.result) ? response.response.result : [];
        const count = response?.code === "200"
          ? getFutureBookCafeReservationCount(rows, response.response?.serverNow)
          : 0;
        if (isCurrent) setBookCafeReservationCount(count);
      } catch {
        if (isCurrent) setBookCafeReservationCount(0);
      }
    };

    void loadBookCafeReservationCount();
    return () => {
      isCurrent = false;
    };
  }, [props.selectedMenu, props.userState.isAuth, props.userState.userId]);

  useEffect(() => {
    let isCurrent = true;

    const loadOverdueBookLoanCount = async () => {
      if (!props.userState.isAuth || !props.userState.userId) {
        if (isCurrent) setOverdueBookLoanCount(0);
        return;
      }

      try {
        const response = await mainApi.get_my_active_book_loans({ userId: props.userState.userId });
        const rows = Array.isArray(response?.response?.result) ? response.response.result : [];
        const count = response?.code === "200" ? getOverdueBookLoanCount(rows) : 0;
        if (isCurrent) setOverdueBookLoanCount(count);
      } catch {
        if (isCurrent) setOverdueBookLoanCount(0);
      }
    };

    void loadOverdueBookLoanCount();
    window.addEventListener(BOOK_LOANS_CHANGED_EVENT, loadOverdueBookLoanCount);
    return () => {
      isCurrent = false;
      window.removeEventListener(BOOK_LOANS_CHANGED_EVENT, loadOverdueBookLoanCount);
    };
  }, [props.selectedMenu, props.userState.isAuth, props.userState.userId]);

  const isMobile = props.winSize === "xs";
  const isMainMenu = props.selectedMenu === "/mobilemain";
  const menuName = getMenuName(props.selectedMenu);
  const homePath = props.selectedMenu.startsWith("/reservationbookcafe/guide/")
    ? "/reservationbookcafe"
    : "/mobilemain";
  const bookCafeTicketPath = "/reservationbookcafeend";
  const bookCafeTicketLabel = bookCafeReservationCount > 0
    ? `북카페 예약 현황 (예약 ${bookCafeReservationCount}건)`
    : "북카페 예약 현황 (예약 없음)";
  const overdueBookLoanLabel = overdueBookLoanCount > 0
    ? `반납일 지난 도서 ${overdueBookLoanCount}권`
    : "반납일 지난 도서 없음";

  return (
    <Box sx={{ flexGrow: 1, position: "sticky", top: "0", zIndex: "700" }}>
      {/* 상단 고정메뉴 Component */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#f8f9fa",
          boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* 우측 메뉴 Drawer */}
        <Drawer
          sx={{
            zIndex: (theme) => theme.zIndex.appBar + 1,
            width: isMobile ? "100vw" : "375px",
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isMobile ? "100vw" : "375px",
              boxSizing: "border-box",
              overflowY: "auto",
              overflowX: "hidden",
              borderLeft: "1px solid #ededee",
              backgroundColor: "#ffffff",
              color: "#17151f",
            },
          }}
          anchor="right"
          open={props.isMenuOpen}
          variant="temporary"
          ModalProps={{
            keepMounted: true,
          }}
          onClose={props.closeMenu}
          className="main-sidebar sidebar-dark-primary"
        >
          <LeftMenu
            ref={LeftMenuRef}
            userState={props.userState}
            selectedMenu={props.selectedMenu}
            move={move}
            closeMenu={props.closeMenu}
          />
        </Drawer>
        {/* 우측 메뉴 Drawer */}
        {/* 상단 툴바 */}
        <Toolbar
          sx={{
            minHeight: "44px !important",
            display: "flex",
            justifyContent: "flex-start",
            pr: "8px !important",
          }}
        >
          {!isMainMenu && (
            <Button
              aria-label={`${menuName} 홈으로 이동`}
              onClick={() => navigate(homePath)}
              startIcon={<ChevronLeftRoundedIcon />}
              sx={{
                minWidth: 0,
                px: "4px",
                py: 0,
                color: "#17151f",
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: 1,
                textTransform: "none",
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": {
                  ml: 0,
                  mr: "2px",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "26px",
                },
              }}
            >
              {menuName}
            </Button>
          )}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              aria-label={overdueBookLoanLabel}
              onClick={() => navigate("/mobilebookstats")}
              sx={{ width: "40px", height: "40px", p: "8px", mr: "-8px", color: "#2E3341" }}
            >
              <Badge
                badgeContent={overdueBookLoanCount}
                color="error"
                invisible={overdueBookLoanCount === 0}
                max={9}
                overlap="circular"
              >
                <HeaderBookIcon />
              </Badge>
            </IconButton>
            <IconButton
              color="inherit"
              aria-label={bookCafeTicketLabel}
              onClick={() => navigate(bookCafeTicketPath)}
              sx={{ width: "40px", height: "40px", p: "8px", mr: "2px", color: "#2E3341" }}
            >
              <Badge
                badgeContent={bookCafeReservationCount}
                color="error"
                invisible={bookCafeReservationCount === 0}
                max={9}
                overlap="circular"
              >
                <HeaderTicketIcon />
              </Badge>
            </IconButton>
            <IconButton
              color="inherit"
              aria-label={props.isMenuOpen ? "전체 메뉴 닫기" : "전체 메뉴 열기"}
              aria-expanded={props.isMenuOpen}
              onClick={clickhandleDrawer}
              edge="end"
              sx={{ width: "40px", height: "40px", p: "7px", mr: 0, color: "#2E3341", display: "flex" }}
            >
              <MenuIcon
                sx={{ color: "#2E3341", width: "26px", height: "26px" }}
              />
            </IconButton>
          </Box>
        </Toolbar>
        {/* 상단 툴바 */}
      </AppBar>
    </Box>
  );
};

export default forwardRef(Header);
